import { Descendant, BaseEditor } from "slate";
import { ReactEditor } from "slate-react";
import type { EntityRef } from "@common/schemas/chatEntityRef.schema";
import { VariableElement } from "@components/forms/VariableTextField/variableTextTypes";

export interface CustomText {
    text: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
}

export interface MentionElement {
    type: "mention";
    userId: number;
    name: string;
    children: CustomText[];
}

export interface LinkElement {
    type: "link";
    url: string;
    children: CustomText[];
}

export type EntityRefElement = EntityRef & {
    type: "entityRef";
    children: CustomText[];
};

export type SlateInlineChild =
    | CustomText
    | MentionElement
    | LinkElement
    | EntityRefElement
    | VariableElement;

export interface ParagraphElement {
    type: "paragraph";
    children: SlateInlineChild[];
}

export type ListType = "bulleted-list" | "numbered-list";

export interface ListItemElement {
    type: "list-item";
    checked?: boolean;
    children: Array<
        ParagraphElement | BulletedListElement | NumberedListElement
    >;
}

export interface BulletedListElement {
    type: "bulleted-list";
    children: ListItemElement[];
}

export interface NumberedListElement {
    type: "numbered-list";
    children: ListItemElement[];
}

export type CustomElement =
    | ParagraphElement
    | ListItemElement
    | BulletedListElement
    | NumberedListElement
    | MentionElement
    | LinkElement
    | EntityRefElement
    | VariableElement;

const IS_INLINE_BY_TYPE: Record<CustomElement["type"], boolean> = {
    paragraph: false,
    "list-item": false,
    "bulleted-list": false,
    "numbered-list": false,
    mention: true,
    link: true,
    entityRef: true,
    variable: true,
};

export const isInlineElement = (element: CustomElement): boolean =>
    IS_INLINE_BY_TYPE[element.type];

declare module "slate" {
    interface CustomTypes {
        Editor: BaseEditor & ReactEditor;
        Element: CustomElement;
        Text: CustomText;
    }
}

export type SlateValue = Descendant[];

export const createEmptySlateValue = (): SlateValue => [
    {
        type: "paragraph",
        children: [{ text: "" }],
    },
];
